import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { getLanguageLabel } from "@/lib/studio-constants";
import { Globe, MoreVertical, Plus, Star, Trash2 } from "lucide-react";
import { type ReactNode } from "react";
import { BuilderSection } from "./studio/builder-section";

interface BaseVariant {
  languageCode: string;
  isDefault: boolean;
}

interface LanguageVariantsSectionProps<T extends BaseVariant> {
  variants: T[];
  activeVariantIndex: number;
  onActiveVariantChange: (index: number) => void;
  availableLanguages: readonly { code: string; label: string }[];
  onAddVariant: (langCode: string) => void;
  onSetDefault?: (index: number) => void;
  pendingRemoveIndex: number | null;
  onRequestRemove: (index: number) => void;
  onConfirmRemove: () => void;
  onCancelRemove: () => void;
  isReadOnly?: boolean;
  removeWarningText?: string;
  stepNumber?: number;
  children: ReactNode;
}

export function LanguageVariantsSection<T extends BaseVariant>({
  variants,
  activeVariantIndex,
  onActiveVariantChange,
  availableLanguages,
  onAddVariant,
  onSetDefault,
  pendingRemoveIndex,
  onRequestRemove,
  onConfirmRemove,
  onCancelRemove,
  isReadOnly,
  removeWarningText,
  stepNumber,
  children,
}: LanguageVariantsSectionProps<T>) {
  const activeVariant = variants[activeVariantIndex] ?? null;
  const pendingVariant =
    pendingRemoveIndex !== null ? variants[pendingRemoveIndex] : null;

  return (
    <>
      <BuilderSection
        title="Language Variants"
        icon={Globe}
        stepNumber={stepNumber}
        badge={`${variants.length} language${variants.length !== 1 ? "s" : ""}`}
        disabled={isReadOnly}
      >
        <div className="flex items-center gap-2 flex-wrap border-b pb-3">
          {variants.map((v, i) => (
            <div key={v.languageCode} className="flex items-center gap-1">
              <Button
                // Not a submit button: builders render this inside their form.
                type="button"
                variant={i === activeVariantIndex ? "default" : "outline"}
                size="sm"
                onClick={() => onActiveVariantChange(i)}
                data-testid={`tab-variant-${v.languageCode}`}
              >
                <Globe className="h-3.5 w-3.5 mr-1.5" />
                {getLanguageLabel(v.languageCode)}
                {v.isDefault && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 text-[10px] no-default-hover-elevate no-default-active-elevate"
                  >
                    Primary
                  </Badge>
                )}
              </Button>
              {!v.isDefault && !isReadOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-1.5"
                      data-testid={`button-variant-menu-${v.languageCode}`}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {onSetDefault && (
                      <DropdownMenuItem
                        onClick={() => onSetDefault(i)}
                        data-testid={`button-set-primary-${v.languageCode}`}
                      >
                        <Star className="h-3.5 w-3.5 mr-2" />
                        Set as Primary
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => onRequestRemove(i)}
                      className="text-destructive focus:text-destructive"
                      data-testid={`button-remove-variant-${v.languageCode}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
          {availableLanguages.length > 0 && !isReadOnly && (
            <Select onValueChange={onAddVariant}>
              <SelectTrigger
                className="w-auto gap-1"
                data-testid="button-add-language"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm">Add Language</span>
              </SelectTrigger>
              <SelectContent>
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {activeVariant && children}
      </BuilderSection>

      <AlertDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) onCancelRemove();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove language variant?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingVariant && (
                <>
                  This will remove the{" "}
                  {getLanguageLabel(pendingVariant.languageCode)} variant
                  {removeWarningText ||
                    " and all its content. This cannot be undone until you save."}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove-variant">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmRemove}
              data-testid="button-confirm-remove-variant"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
